import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone, MapPin } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import OptimizedImage from '../../components/OptimizedImage';

import SEO from '../../components/SEO';

const ZonePaysArve = () => {
  const cities = [
    'Bonneville', 'Cluses', 'Scionzier', 'Thyez', 'Marignier', 
    'Marnaz', 'Mont-Saxonnex', 'La Roche-sur-Foron', 
    'Saint-Pierre-en-Faucigny', 'Ayze', 'Vougy', 'Magland'
  ];

  const services = [
    {
      title: 'Débarras de maison',
      description: 'Vide-maison complet pour les propriétés de la vallée de l\'Arve.'
    },
    {
      title: 'Débarras d\'appartement',
      description: 'Intervention dans tous les quartiers de Bonneville, Cluses et environs.'
    },
    {
      title: 'Débarras cave et grenier',
      description: 'Désencombrement des espaces de stockage des maisons et résidences.'
    },
    {
      title: 'Débarras succession',
      description: 'Accompagnement respectueux pour les familles du Pays de l\'Arve.'
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO 
        title="Débarras Vallée de l'Arve : Bonneville, Cluses | DEBECO"
        description="Débarras professionnel dans la Vallée de l'Arve. Bonneville, Cluses, La Roche-sur-Foron et communes environnantes."
        canonical="/zones/pays-arve"
        ogImage="/images/pays-arve.jpg"
        jsonLd={{"@context":"https://schema.org","@type":"BreadcrumbList","itemListElement":[{"@type":"ListItem","position":1,"name":"Accueil","item":"https://debeco.fr/"},{"@type":"ListItem","position":2,"name":"Zones d'intervention","item":"https://debeco.fr/zones"},{"@type":"ListItem","position":3,"name":"Vallée de l'Arve"}]}}
      />
      {/* Hero */}
      <section className="hero-banner text-white py-16">
        <div className="container mx-auto px-4 text-center relative z-10">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Débarras dans la Vallée de l'Arve</h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto">
            Vallée de l'Arve, de Bonneville à Cluses et les communes environnantes
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start mb-16">
              {/* Image */}
              <div className="relative h-96 rounded-2xl overflow-hidden shadow-2xl">
                <OptimizedImage 
                  src="/images/pays-arve.jpg"
                  webpSrc="/images/pays-arve.webp"
                  alt="Vallée de l'Arve avec vue sur les sommets alpins, zone d'intervention DEBECO entre Bonneville et Cluses"
                  className="w-full h-full object-cover"
                  width="800"
                  height="533"
                  loading="eager"
                  data-testid="zone-pays-arve-image"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/60 to-transparent"></div>
                <div className="absolute bottom-6 left-6">
                  <div className="w-16 h-16 bg-white rounded-xl flex items-center justify-center shadow-lg">
                    <MapPin className="w-10 h-10 text-blue-900" />
                  </div>
                </div>
              </div>

              {/* Content */}
              <div>
                <h2 className="text-3xl font-bold text-blue-900 mb-6">Votre partenaire débarras dans la Vallée de l'Arve</h2>
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  DEBECO intervient dans toute la vallée de l'Arve, de Bonneville à Cluses et au-delà. Cette vallée dynamique et industrielle bénéficie de notre service de débarras professionnel adapté aux besoins des particuliers et des entreprises.
                </p>
                <p className="text-gray-700 text-lg leading-relaxed mb-8">
                  Que vous soyez dans un appartement en centre-ville, une maison en périphérie ou un local commercial, nous intervenons rapidement et efficacement pour tous vos besoins de débarras.
                </p>

                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/contact">
                    <Button className="bg-gradient-to-r from-emerald-600 to-emerald-500 hover:from-emerald-700 hover:to-emerald-600 text-white shadow-lg w-full sm:w-auto">
                      Demander un devis gratuit
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                  <a href="tel:0451401617">
                    <Button variant="outline" className="border-2 border-blue-900 text-blue-900 hover:bg-blue-900 hover:text-white w-full sm:w-auto">
                      <Phone className="mr-2 w-4 h-4" />
                      04 51 40 16 17
                    </Button>
                  </a>
                </div>
              </div>
            </div>

            {/* Cities */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-blue-900 mb-8 text-center">Villes couvertes</h2>
              <div className="flex flex-wrap justify-center gap-3">
                {cities.map((city, index) => (
                  <span key={index} className="bg-blue-100 text-blue-900 px-4 py-2 rounded-full text-sm font-medium">
                    {city}
                  </span>
                ))}
              </div>
            </div>

            {/* Services */}
            <div className="mb-16">
              <h2 className="text-3xl font-bold text-blue-900 mb-8 text-center">Nos services dans la Vallée de l'Arve</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {services.map((service, index) => (
                  <Card key={index} className="border-2 hover:border-emerald-500 hover:shadow-xl transition-all">
                    <CardContent className="p-6">
                      <h3 className="font-bold text-xl text-blue-900 mb-3">{service.title}</h3>
                      <p className="text-gray-600">{service.description}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {[
                { number: '48-72h', label: 'Délai d\'intervention' },
                { number: '12+', label: 'Communes couvertes' },
                { number: '100%', label: 'Clients satisfaits' },
                { number: '7j/7', label: 'Disponibilité' }
              ].map((stat, index) => (
                <Card key={index} className="text-center border-2 hover:border-emerald-500 hover:shadow-xl transition-all">
                  <CardContent className="p-6">
                    <p className="text-4xl font-bold text-blue-900 mb-2">{stat.number}</p>
                    <p className="text-gray-600">{stat.label}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Maillage interne */}
      <section className="py-12 bg-gray-50" data-testid="maillage-interne">
        <div className="container mx-auto px-4">
          <h2 className="text-2xl font-bold text-blue-900 mb-6">Nos services dans la Vallée de l'Arve</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link to="/services/maison" className="block p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <p className="font-semibold text-blue-900">Débarras de maison</p>
              <p className="text-sm text-gray-600 mt-1">Vide-maison complet, du grenier à la cave</p>
            </Link>
            <Link to="/services/cave-grenier" className="block p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <p className="font-semibold text-blue-900">Débarras cave, grenier et garage</p>
              <p className="text-sm text-gray-600 mt-1">Désencombrement complet avec tri et recyclage</p>
            </Link>
            <Link to="/devis" className="block p-4 bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow">
              <p className="font-semibold text-blue-900">Estimation gratuite en ligne</p>
              <p className="text-sm text-gray-600 mt-1">Obtenez un devis en quelques minutes</p>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-blue-900 to-emerald-800 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">Besoin d'un débarras dans la Vallée de l'Arve ?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Contactez-nous pour un devis gratuit et une intervention rapide dans toute la vallée
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100 shadow-xl text-lg px-8 py-6 w-full sm:w-auto">
                Demander un devis
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href="tel:0451401617">
              <Button size="lg" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900 text-lg px-8 py-6 w-full sm:w-auto">
                <Phone className="mr-2 w-5 h-5" />
                04 51 40 16 17
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ZonePaysArve;
